import type { OrgTypeDTO } from '@/services/organization.service'
import { collect, diff, type FieldErrors, requiredText } from './form-rules'

/** The type dialog as edited (TO-389 organization-admin-ui REQ-009). */
export interface TypeFormValues {
	code: string
	name: string
	isPhysical: boolean
	canHaveUsers: boolean
	sortOrder: string
	/** "Any parent type": sends `allowedParentTypeCodes: null`. */
	anyParent: boolean
	allowedParentTypeCodes: string[]
}

export const EMPTY_TYPE_FORM: TypeFormValues = {
	code: '',
	name: '',
	isPhysical: false,
	canHaveUsers: true,
	sortOrder: '',
	anyParent: true,
	allowedParentTypeCodes: [],
}

const MAX_PARENT_TYPES = 50

const sortOrderError = (text: string): string | null => {
	if (text.trim() === '') return null
	const n = Number(text)
	return Number.isInteger(n) && n >= 0 && n <= 10_000 ? null : 'validation.sortOrder'
}

export const typeFormErrors = (v: TypeFormValues): FieldErrors =>
	collect({
		code: requiredText(v.code, 64),
		name: requiredText(v.name, 200),
		sortOrder: sortOrderError(v.sortOrder),
		allowedParentTypeCodes:
			!v.anyParent && v.allowedParentTypeCodes.length > MAX_PARENT_TYPES ? 'validation.tooMany' : null,
	})

const mutableBody = (v: TypeFormValues): Record<string, unknown> => ({
	name: v.name.trim(),
	isPhysical: v.isPhysical,
	canHaveUsers: v.canHaveUsers,
	sortOrder: v.sortOrder.trim() === '' ? 0 : Number(v.sortOrder),
	allowedParentTypeCodes: v.anyParent ? null : v.allowedParentTypeCodes,
})

export const buildTypeCreateBody = (v: TypeFormValues): Record<string, unknown> => ({
	code: v.code.trim(),
	...mutableBody(v),
})

export const typeToForm = (t: OrgTypeDTO): TypeFormValues => ({
	code: t.code,
	name: t.name,
	isPhysical: t.isPhysical,
	canHaveUsers: t.canHaveUsers,
	sortOrder: String(t.sortOrder),
	anyParent: t.allowedParentTypeCodes === null,
	allowedParentTypeCodes: t.allowedParentTypeCodes ?? [],
})

/** Only the changed fields; `code` is read-only after create and never sent. */
export const buildTypePatch = (original: OrgTypeDTO, v: TypeFormValues): Record<string, unknown> =>
	diff(mutableBody(typeToForm(original)), mutableBody(v))
